import * as React from 'react';
import {styledDiv} from '@packages/utilities/styled';
import {Plugins, Expressions} from '@packages/systems/wfdl';
import {
  ECall,
  EExtend,
  ERecord,
  EUnion,
  ECase,
  EList,
  EText,
  EHole,
  functionOf,
  TList,
  TNumber,
  TRecord,
  TText,
  TTypeReference,
  TUnion,
  Expression,
} from '@packages/systems/wfdl-ast';
import {
  type Qualified,
  qualify,
  pluginName,
  typeName,
  iconName,
} from '@packages/systems/wfdl-Qualified';
import {
  forall,
  tyapp,
  tyfun,
  at,
  func,
  call,
  call1,
  _$,
  $,
} from '@packages/utilities/wfdl-helpers';
import {
  Fail,
  TEffect,
  bind,
  pure,
  Pure,
} from '@packages/systems/plugins/Effect';
import {unwrapSecret} from '@packages/systems/plugins/Secret';
import {TOption} from '@packages/systems/plugins/Option';
import {concat, isEmpty, append} from '@packages/systems/plugins/Text';
import {foldRight, prepend} from '@packages/systems/plugins/List';
import {
  AUTHENTICATION,
  LOGIC_BLOCK_CATEGORIES,
} from '@packages/systems/flows/shared';
import {LocationIcon} from '@packages/systems/spring';
import {RequestAuthenticationType} from './types/RequestAuthentication';
import {RequestAuthenticationQueryParamType} from './types/RequestAuthenticationQueryParam';
import {RequestAuthenticationHeaderType} from './types/RequestAuthenticationHeader';
import {RequestHeadersType} from './types/RequestHeaders';
import {RequestURLType} from './types/RequestURL';
import {NameAndValueType} from './types/NameAndValue';
import {RequestMethodType} from './types/RequestMethod';
import {RequestGenericType} from './types/RequestGeneric';

export {guessJsonParser, createJsonParserToEffect} from './guessJsonParser';
export {createParseResponseExpr} from './utils';

// TODO: This component should live in core not here or in Effect.
const FlowIconWrapper = styledDiv(({theme: {colors, gutters}}) => ({
  gridRow: '1 / 3',
  width: gutters.x12 * 3,
  height: gutters.x12 * 3,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: `1px solid ${colors.border}`,
  borderRadius: '4px',
  color: colors.white,
  backgroundColor: colors.accentBackground,
}));

const HttpRequestBlockIcon = () => (
  <FlowIconWrapper>
    <LocationIcon size={20} />
  </FlowIconWrapper>
);

const httpPluginName = pluginName('Http');
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
const name = <nameType extends unknown>(n: nameType): Qualified<nameType> =>
  qualify(httpPluginName, n);

const error = TTypeReference('error');

const presetAuthentication = EUnion({
  tag: AUTHENTICATION.TYPE.NONE,
  value: ERecord({}),
});

const httpRequest = call1(_$('Effect', 'HTTP request'));

const TUrl = TRecord({
  protocol: TText,
  username: TText,
  password: TText,
  hostname: TText,
  port: TOption(TNumber),
  pathname: TText,
  params: TList(TRecord({Name: TText, Value: TText})),
  hash: TText,
});

const callEncodeURIComponent = call1(_$('Http', 'Encode URI Component'));

const processHeaders = (init: Expression) =>
  foldRight(
    func(
      ['pair', 'headers'],
      ECase({
        value: isEmpty(at($('pair'), ['Name'])),
        cases: {
          True: func(['_'], $('headers')),
          False: func(
            ['_'],
            ECase({
              value: isEmpty(at($('pair'), ['Value'])),
              cases: {
                True: func(['_'], $('headers')),
                False: func(
                  ['_'],
                  prepend(
                    ERecord({
                      name: at($('pair'), ['Name']),
                      value: at($('pair'), ['Value']),
                    }),
                    $('headers')
                  )
                ),
              },
            })
          ),
        },
      })
    ),
    init,
    at($('config'), ['Headers'])
  );

export const plugin = Plugins.PrivilegedPlugin({
  dataTypes: {
    'Request Authentication': RequestAuthenticationType,
    'Request Authentication Query Parameter':
      RequestAuthenticationQueryParamType,
    'Request Authentication Header': RequestAuthenticationHeaderType,
    'Request Headers': RequestHeadersType,
    'Request URL': RequestURLType,
    'Name & Value': NameAndValueType,
    'Request Method': RequestMethodType,
    'Request Generic': RequestGenericType,
  },
  values: {
    'Make HTTP request': {
      envType: {
        dataType: forall(
          ['error', 'output'],
          functionOf(
            tyapp({
              con: qualify(pluginName('Http'), typeName('Request Generic')),
              args: [
                TUnion({'URL Decode Error': TText}, error),
                TTypeReference('output'),
              ],
            }),
            TEffect(
              TUnion({'URL Decode Error': TText}, error),
              TTypeReference('output')
            )
          )
        ),
        exported: 'hidden',
      },
      // TODO: Use config.Authentication to set credentials
      expression: func(
        ['config'],
        bind(
          ECase({
            value: at($('config'), ['Authentication']),
            cases: {
              None: func(
                ['_'],
                pure(
                  ERecord({
                    url: at($('config'), ['URL']),
                    method: at($('config'), ['Method']),
                    headers: processHeaders(EList([])),
                  })
                )
              ),
              'API token': func(
                ['token config'],
                ECase({
                  value: at($('token config'), ['Add to']),
                  cases: {
                    // The user has selected a custom header for the auth token
                    Header: func(
                      ['Header name'],
                      pure(
                        ERecord({
                          url: at($('config'), ['URL']),
                          method: at($('config'), ['Method']),
                          headers: processHeaders(
                            EList([
                              ERecord({
                                name: $('Header name'),
                                value: unwrapSecret(
                                  at($('token config'), ['Credential'])
                                ),
                              }),
                            ])
                          ),
                        })
                      )
                    ),
                    'Query params': func(
                      ['Parameter Name'],
                      bind(
                        ECall({
                          fun: _$('Http', 'Decode URL'),
                          arg: at($('config'), ['URL']),
                        }),
                        func(
                          ['URL'],
                          pure(
                            ERecord({
                              url: ECall({
                                fun: _$('Http', 'Encode URL'),
                                arg: call(_$('Http', 'Add Query Parameter'), [
                                  $('Parameter Name'),
                                  unwrapSecret(
                                    at($('token config'), ['Credential'])
                                  ),
                                  $('URL'),
                                ]),
                              }),
                              method: at($('config'), ['Method']),
                              headers: processHeaders(EList([])),
                            })
                          )
                        )
                      )
                    ),
                  },
                })
              ),
              'Username & password': func(
                ['secret'],
                pure(
                  ERecord({
                    url: at($('config'), ['URL']),
                    method: at($('config'), ['Method']),
                    headers: processHeaders(
                      EList([
                        ERecord({
                          name: EText('Authorization'),
                          value: append(
                            EText('Basic '),
                            unwrapSecret($('secret'))
                          ),
                        }),
                      ])
                    ),
                  })
                )
              ),
            },
          }),
          func(
            ['Request Input'],
            bind(
              httpRequest($('Request Input')),
              at($('config'), ['Response Parser'])
            )
          )
        )
      ),
      icon: name(iconName('SendHttpRequestBlock')),
    },
    'Decode URL': {
      envType: {
        dataType: forall(
          ['error'],
          functionOf(
            TText,
            TEffect(TUnion({'URL Decode Error': TText}, error), TUrl)
          )
        ),
        exported: 'no',
      },
      // @ts-expect-error - TS7006 - Parameter 'text' implicitly has an 'any' type.
      value: (text) => {
        try {
          const url = new URL(text);
          const params = [...url.searchParams]
            .reverse()
            .map((param) => ({Name: param[0], Value: param[1]}));
          return Pure({
            protocol: url.protocol,
            username: url.username,
            password: url.password,
            hostname: url.hostname,
            port: url.port
              ? {tag: 'Some', val: Number(url.port)}
              : {tag: 'None', val: {}},
            pathname: url.pathname,
            params,
            hash: url.hash,
          });
        } catch (e: any) {
          return Fail({tag: 'URL Decode Error', val: e.message});
        }
      },
    },
    'Encode URI Component': {
      envType: {
        dataType: functionOf(TText, TText),
        exported: 'no',
      },
      value: encodeURIComponent,
    },
    'Add Query Parameter': {
      envType: {
        dataType: tyfun([TText, TText, TUrl], TUrl),
        exported: 'no',
      },
      expression: func(
        ['key', 'value', 'url'],
        EExtend({
          record: $('url'),
          prop: 'params',
          value: prepend(
            ERecord({
              Name: callEncodeURIComponent($('key')),
              Value: callEncodeURIComponent($('value')),
            }),
            at($('url'), ['params'])
          ),
        })
      ),
    },
    'Encode URL': {
      envType: {
        dataType: functionOf(TUrl, TText),
        exported: 'no',
      },
      expression: func(
        ['URL'],
        concat([
          at($('URL'), ['protocol']),
          EText('//'),
          ECase({
            value: isEmpty(at($('URL'), ['username'])),
            cases: {
              True: func(['_'], EText('')),
              False: func(
                ['_'],
                concat([
                  at($('URL'), ['username']),
                  EText(':'),
                  at($('URL'), ['password']),
                  EText('@'),
                ])
              ),
            },
          }),
          at($('URL'), ['hostname']),
          ECase({
            value: at($('URL'), ['port']),
            cases: {
              None: func(['_'], EText('')),
              Some: _$('Number', 'To Text'),
            },
          }),
          at($('URL'), ['pathname']),
          foldRight(
            func(
              ['param', 'acc'],
              concat([
                $('acc'),
                ECase({
                  value: isEmpty($('acc')),
                  cases: {
                    True: func(['_'], EText('?')),
                    False: func(['_'], EText('&')),
                  },
                }),
                at($('param'), ['Name']),
                EText('='),
                at($('param'), ['Value']),
              ])
            ),
            EText(''),
            at($('URL'), ['params'])
          ),
          at($('URL'), ['hash']),
        ])
      ),
    },
  },
  icons: {
    SendHttpRequestBlock: HttpRequestBlockIcon,
    SendHttpRequestPanel: HttpRequestBlockIcon,
  },
  valuePresets: {
    Request: {
      factory: ECall({
        fun: Expressions.qualifiedVariable('Http', 'Make HTTP request'),
        arg: ERecord({
          URL: EHole(),
          Authentication: presetAuthentication,
          Headers: EList([]),
          Method: EUnion({tag: 'GET', value: ERecord({})}),
          'Response Parser': _$('Effect', 'pure'),
        }),
      }),
      label: 'Make HTTP request',
      icon: {
        medium: name(iconName('SendHttpRequestPanel')),
        small: name(iconName('SendHttpRequestPanel')),
      },
      category: LOGIC_BLOCK_CATEGORIES.Actions,
    },
  },
});
